/**
 * AdminPanel - Panel administracyjny do zarządzania użytkownikami i rolami
 * wojskaspecjalne.pl
 * 
 * Używa komponentów Radix UI dla spójnego design system
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, Users, BookOpen, Plus, Calendar, PanelsTopLeft, X, UserCog, GraduationCap, Heart, ClipboardList, MapPin } from 'lucide-react';
import { AdminOnly, ModeratorOnly } from '../components/RoleGuard';
import { RoleBadgeList } from '../components/RoleBadge';
import { getAllUsersWithRoles, getAllRoles, assignRole, removeRole } from '../lib/roleService';
import { 
  getBooksWithTagsAndCategories, 
  deleteBook, 
  publishBook, 
  archiveBook, 
  getLibraryStats,
  type Book,
  type BookWithTagsAndCategories
} from '../lib/libraryService';
import { getAllEvents, deleteEvent, publishEvent, cancelEvent, getEventsStats } from '../lib/eventService';
import { getAllInstructors, deleteInstructor, getInstructorsStats, type Instructor } from '../lib/instructorService';
import { createTraining, getAllTrainings, deleteTraining, publishTraining, cancelTraining, getTrainingsStats, type Training } from '../lib/trainingService';
import { getTrainingRegistrationsWithProfiles, type TrainingRegistrationWithProfile } from '../lib/trainingRegistrationsService';
import { getAllTrainingTypes, type TrainingType } from '../lib/trainingCatalogService';
import { getAllShootingRanges, type ShootingRange } from '../lib/shootingRangesService';
import { BookForm } from '../components/library/BookForm';
import { InstructorForm } from '../components/instructors/InstructorForm';
import { TrainingCatalogTab } from '../components/admin/TrainingCatalogTab';
import { ShootingRangesTab } from '../components/admin/ShootingRangesTab';
import { AdminAuthorsTab } from './AdminAuthors';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole } from '../hooks/useUserRole';
import type { UserWithRoles, RoleName, Event } from '../types/database.types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Input,
  Label,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Switch,
  Alert,
  AlertDescription,
  Separator,
} from '../components/ui';
import { ConfirmDialog } from '../components/dialogs';
import './AdminPanel.css';

type TabType =
  | 'dashboard'
  | 'users'
  | 'library'
  | 'authors'
  | 'events'
  | 'trainingCatalog'
  | 'instructors'
  | 'shootingRanges'
  | 'trainings';

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { roles } = useUserRole();
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const isAdmin = roles.some(role => role.role_name === 'admin');
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserWithRoles | null>(null);
  const [assigningRole, setAssigningRole] = useState(false);
  
  // Library state
  const [books, setBooks] = useState<BookWithTagsAndCategories[]>([]);
  const [libraryStats, setLibraryStats] = useState<any>(null);
  const [showBookForm, setShowBookForm] = useState(false);
  const [editingBookId, setEditingBookId] = useState<string | null>(null);
  const [libraryLoading, setLibraryLoading] = useState(false);
  
  // Events state
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsStats, setEventsStats] = useState<any>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  
  // Instructors state
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [instructorsStats, setInstructorsStats] = useState<any>(null);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [showInstructorForm, setShowInstructorForm] = useState(false);
  const [editingInstructorId, setEditingInstructorId] = useState<string | null>(null);
  
  // Trainings state
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [trainingsStats, setTrainingsStats] = useState<any>(null);
  const [trainingsLoading, setTrainingsLoading] = useState(false);
  const [createTrainingDialogOpen, setCreateTrainingDialogOpen] = useState(false);
  const [creatingTraining, setCreatingTraining] = useState(false);
  const [trainingFormInstructors, setTrainingFormInstructors] = useState<Instructor[]>([]);
  const [trainingFormTypes, setTrainingFormTypes] = useState<TrainingType[]>([]);
  const [trainingFormRanges, setTrainingFormRanges] = useState<ShootingRange[]>([]);
  const [trainingForm, setTrainingForm] = useState<{
    title: string;
    type_id: string;
    instructor_id: string;
    difficulty_level: 'podstawowy' | 'średni' | 'zaawansowany';
    start_date: string;
    end_date: string;
    price_amount: number;
    max_participants: number;
    use_range: boolean;
    shooting_range_id: string;
    location_name: string;
    location_address: string;
    location_city: string;
    location_country: string;
    is_waitlist_enabled: boolean;
    status: 'draft' | 'published';
  }>({
    title: '',
    type_id: '',
    instructor_id: '',
    difficulty_level: 'podstawowy',
    start_date: '',
    end_date: '',
    price_amount: 0,
    max_participants: 20,
    use_range: true,
    shooting_range_id: '',
    location_name: '',
    location_address: '',
    location_city: '',
    location_country: 'Poland',
    is_waitlist_enabled: true,
    status: 'draft',
  });
  const [registrationsDialog, setRegistrationsDialog] = useState<{
    open: boolean;
    trainingId: string;
    trainingTitle: string;
  } | null>(null);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [registrations, setRegistrations] = useState<TrainingRegistrationWithProfile[]>([]);
  const [registrationsError, setRegistrationsError] = useState<string | null>(null);
  
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );
  
  // Confirm dialogs state
  const [removeRoleDialog, setRemoveRoleDialog] = useState<{
    open: boolean;
    userId: string;
    roleName: RoleName;
  } | null>(null);
  const [deleteBookDialog, setDeleteBookDialog] = useState<{
    open: boolean;
    bookId: string;
    bookTitle: string;
  } | null>(null);

  const isValidTab = (tab: string | null): tab is TabType => {
    if (!tab) return false;
    return [
      'dashboard',
      'users',
      'library',
      'authors',
      'events',
      'trainingCatalog',
      'instructors',
      'shootingRanges',
      'trainings',
    ].includes(tab);
  };

  const activateTab = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'dashboard') next.delete('tab');
      else next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (isValidTab(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'library') {
      loadLibraryData();
    } else if (activeTab === 'events') {
      loadEventsData();
    } else if (activeTab === 'instructors') {
      loadInstructorsData();
    } else if (activeTab === 'trainings') {
      loadTrainingsData();
    }
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, rolesData] = await Promise.all([
        getAllUsersWithRoles(),
        getAllRoles(),
      ]);

      setUsers(usersData);
      setAvailableRoles(rolesData);
    } catch (error) {
      console.error('Error loading admin panel data:', error);
      showMessage('error', 'Błąd podczas ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  const loadLibraryData = async () => {
    try {
      setLibraryLoading(true);
      const [booksResult, statsResult] = await Promise.all([
        getBooksWithTagsAndCategories({ includeUnpublished: true }),
        getLibraryStats(),
      ]);

      if (booksResult.success) {
        setBooks(booksResult.data || []);
      }

      if (statsResult.success) {
        setLibraryStats(statsResult.data);
      }
    } catch (error) {
      console.error('Error loading library data:', error);
      showMessage('error', 'Błąd podczas ładowania biblioteki');
    } finally {
      setLibraryLoading(false);
    }
  };


  const loadEventsData = async () => {
    try {
      setEventsLoading(true);
      const [eventsResult, statsResult] = await Promise.all([
        getAllEvents({ includeUnpublished: true }),
        getEventsStats(),
      ]);

      if (eventsResult.success) {
        setEvents(eventsResult.data || []);
      }

      if (statsResult.success) {
        setEventsStats(statsResult.data);
      }
    } catch (error) {
      console.error('Error loading events data:', error);
      showMessage('error', 'Błąd podczas ładowania wydarzeń');
    } finally {
      setEventsLoading(false);
    }
  };

  const loadInstructorsData = async () => {
    try {
      setInstructorsLoading(true);
      const [instructorsResult, statsResult] = await Promise.all([
        getAllInstructors({ includeInactive: true }),
        getInstructorsStats(),
      ]);

      if (instructorsResult.success) {
        setInstructors(instructorsResult.data || []);
      }

      if (statsResult.success) {
        setInstructorsStats(statsResult.data);
      }
    } catch (error) {
      console.error('Error loading instructors data:', error);
      showMessage('error', 'Błąd podczas ładowania instruktorów');
    } finally {
      setInstructorsLoading(false);
    }
  };

  const handleAssignRole = async (userId: string, roleName: RoleName) => {
    if (!user) return;

    try {
      setAssigningRole(true);
      const result = await assignRole(userId, roleName, user.id);

      if (result.success) {
        showMessage('success', `Rola ${roleName} została przypisana`);
        await loadData(); // Odśwież listę
        setSelectedUser(null);
      } else {
        showMessage('error', result.error || 'Błąd podczas przypisywania roli');
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      showMessage('error', 'Błąd podczas przypisywania roli');
    } finally {
      setAssigningRole(false);
    }
  };

  const handleRemoveRole = async (userId: string, roleName: RoleName) => {
    setRemoveRoleDialog({ open: true, userId, roleName });
  };

  const confirmRemoveRole = async () => {
    if (!removeRoleDialog) return;

    try {
      const result = await removeRole(removeRoleDialog.userId, removeRoleDialog.roleName);

      if (result.success) {
        showMessage('success', `Rola ${removeRoleDialog.roleName} została usunięta`);
        await loadData(); // Odśwież listę
      } else {
        showMessage('error', result.error || 'Błąd podczas usuwania roli');
      }
    } catch (error) {
      console.error('Error removing role:', error);
      showMessage('error', 'Błąd podczas usuwania roli');
    } finally {
      setRemoveRoleDialog(null);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const openCreateTrainingGroup = async () => {
    try {
      setCreatingTraining(false);
      setTrainingFormInstructors([]);
      setTrainingFormTypes([]);
      setTrainingFormRanges([]);

      // Reset form (leave defaults)
      setTrainingForm((prev) => ({
        ...prev,
        title: '',
        type_id: '',
        instructor_id: '',
        start_date: '',
        end_date: '',
        price_amount: 0,
        max_participants: 20,
        use_range: true,
        shooting_range_id: '',
        location_name: '',
        location_address: '',
        location_city: '',
        location_country: 'Poland',
        is_waitlist_enabled: true,
        status: 'draft',
      }));

      const [instructorsRes, typesRes, rangesRes] = await Promise.all([
        getAllInstructors({ includeInactive: false, sortByOrder: true }),
        getAllTrainingTypes({ includeInactive: false }),
        getAllShootingRanges({ includeInactive: false, sortByOrder: true }),
      ]);

      if (instructorsRes.success) setTrainingFormInstructors(instructorsRes.data || []);
      if (typesRes.success) setTrainingFormTypes(typesRes.data || []);
      if (rangesRes.success) setTrainingFormRanges(rangesRes.data || []);

      if (!instructorsRes.success) showMessage('error', instructorsRes.error || 'Błąd ładowania instruktorów');
      if (!typesRes.success) showMessage('error', typesRes.error || 'Błąd ładowania typów szkoleń');
      if (!rangesRes.success) showMessage('error', rangesRes.error || 'Błąd ładowania strzelnic');

      setCreateTrainingDialogOpen(true);
    } catch (error: any) {
      console.error('Error opening create training dialog:', error);
      showMessage('error', error?.message || 'Błąd podczas przygotowania formularza');
    }
  };

  const applySelectedRangeToLocation = (rangeId: string) => {
    const r = trainingFormRanges.find((x) => x.id === rangeId);
    if (!r) return;
    setTrainingForm((prev) => ({
      ...prev,
      shooting_range_id: r.id,
      location_name: r.name,
      location_address: r.address || '',
      location_city: r.city || '',
      location_country: r.country || 'Poland',
    }));
  };

  const submitCreateTrainingGroup = async () => {
    if (!user) {
      showMessage('error', 'Musisz być zalogowany');
      return;
    }

    try {
      setCreatingTraining(true);

      if (!trainingForm.title.trim()) {
        showMessage('error', 'Tytuł jest wymagany');
        return;
      }
      if (!trainingForm.start_date) {
        showMessage('error', 'Data rozpoczęcia jest wymagana');
        return;
      }
      if (!trainingForm.end_date) {
        showMessage('error', 'Data zakończenia jest wymagana');
        return;
      }

      const startIso = new Date(trainingForm.start_date).toISOString();
      const endIso = new Date(trainingForm.end_date).toISOString();

      if (new Date(endIso) < new Date(startIso)) {
        showMessage('error', 'Data zakończenia nie może być wcześniejsza niż data rozpoczęcia');
        return;
      }

      const locationName = trainingForm.use_range
        ? trainingForm.location_name
        : trainingForm.location_name.trim();

      if (!locationName) {
        showMessage('error', 'Miejsce szkolenia jest wymagane');
        return;
      }

      const result = await createTraining(
        {
          title: trainingForm.title,
          type_id: trainingForm.type_id || null,
          shooting_range_id: trainingForm.use_range ? trainingForm.shooting_range_id || null : null,
          instructor_id: trainingForm.instructor_id || null,
          difficulty_level: trainingForm.difficulty_level,
          price_amount: Number.isFinite(trainingForm.price_amount) ? trainingForm.price_amount : 0,
          price_currency: 'PLN',
          start_date: startIso,
          end_date: endIso,
          timezone: 'Europe/Warsaw',
          location_name: locationName,
          location_address: trainingForm.location_address || null,
          location_city: trainingForm.location_city || null,
          location_country: trainingForm.location_country || 'Poland',
          max_participants: Number.isFinite(trainingForm.max_participants) ? trainingForm.max_participants : 20,
          is_waitlist_enabled: trainingForm.is_waitlist_enabled,
          status: trainingForm.status,
        },
        user.id
      );

      if (!result.success) {
        showMessage('error', result.error || 'Błąd tworzenia grupy szkoleniowej');
        return;
      }

      showMessage('success', 'Grupa szkoleniowa została utworzona');
      setCreateTrainingDialogOpen(false);
      await loadTrainingsData();
    } catch (error: any) {
      console.error('Error creating training:', error);
      showMessage('error', error?.message || 'Błąd tworzenia grupy szkoleniowej');
    } finally {
      setCreatingTraining(false);
    }
  };


  const openRegistrations = async (trainingId: string, trainingTitle: string) => {
    setRegistrationsDialog({ open: true, trainingId, trainingTitle });
    setRegistrationsError(null);
    setRegistrations([]);
    setRegistrationsLoading(true);

    const result = await getTrainingRegistrationsWithProfiles(trainingId);
    if (!result.success) {
      setRegistrationsError(result.error || 'Błąd pobierania rezerwacji');
      setRegistrationsLoading(false);
      return;
    }

    setRegistrations(result.data || []);
    setRegistrationsLoading(false);
  };

  const getRoleNameFromId = (roleId: string): RoleName | undefined => {
    const role = availableRoles.find((r) => r.id === roleId);
    return role?.name as RoleName | undefined;
  };

  // Library handlers
  const handleAddBook = () => {
    setEditingBookId(null);
    setShowBookForm(true);
  };

  const handleEditBook = (bookId: string) => {
    setEditingBookId(bookId);
    setShowBookForm(true);
  };

  const handleBookFormSuccess = async (book: Book) => {
    showMessage('success', editingBookId ? 'Książka została zaktualizowana' : 'Książka została dodana');
    setShowBookForm(false);
    setEditingBookId(null);
    await loadLibraryData();
  };

  const handleBookFormCancel = () => {
    setShowBookForm(false);
    setEditingBookId(null);
  };

  const handleDeleteBook = async (bookId: string, bookTitle: string) => {
    setDeleteBookDialog({ open: true, bookId, bookTitle });
  };

  const confirmDeleteBook = async () => {
    if (!deleteBookDialog) return;

    try {
      const result = await deleteBook(deleteBookDialog.bookId);
      if (result.success) {
        showMessage('success', 'Książka została usunięta');
        await loadLibraryData();
      } else {
        showMessage('error', result.error || 'Błąd podczas usuwania książki');
      }
    } catch (error) {
      console.error('Error deleting book:', error);
      showMessage('error', 'Błąd podczas usuwania książki');
    } finally {
      setDeleteBookDialog(null);
    }
  };

  const handlePublishBook = async (bookId: string) => {
    try {
      const result = await publishBook(bookId);
      if (result.success) {
        showMessage('success', 'Książka została opublikowana');
        await loadLibraryData();
      } else {
        showMessage('error', result.error || 'Błąd podczas publikowania książki');
      }
    } catch (error) {
      console.error('Error publishing book:', error);
      showMessage('error', 'Błąd podczas publikowania książki');
    }
  };

  const handleArchiveBook = async (bookId: string) => {
    try {
      const result = await archiveBook(bookId);
      if (result.success) {
        showMessage('success', 'Książka została zarchiwizowana');
        await loadLibraryData();
      } else {
        showMessage('error', result.error || 'Błąd podczas archiwizowania książki');
      }
    } catch (error) {
      console.error('Error archiving book:', error);
      showMessage('error', 'Błąd podczas archiwizowania książki');
    }
  };

  // Events handlers
  const handlePublishEvent = async (eventId: string) => {
    try {
      const result = await publishEvent(eventId);
      if (result.success) {
        showMessage('success', 'Wydarzenie zostało opublikowane');
        await loadEventsData();
      } else {
        showMessage('error', result.error || 'Błąd podczas publikowania wydarzenia');
      }
    } catch (error) {
      console.error('Error publishing event:', error);
      showMessage('error', 'Błąd podczas publikowania wydarzenia');
    }
  };

  const handleCancelEvent = async (eventId: string) => {
    try {
      const result = await cancelEvent(eventId);
      if (result.success) {
        showMessage('success', 'Wydarzenie zostało anulowane');
        await loadEventsData();
      } else {
        showMessage('error', result.error || 'Błąd podczas anulowania wydarzenia');
      }
    } catch (error) {
      console.error('Error cancelling event:', error);
      showMessage('error', 'Błąd podczas anulowania wydarzenia');
    }
  };

  const handleDeleteEvent = async (eventId: string, eventTitle: string) => {
    if (!confirm(`Czy na pewno chcesz usunąć wydarzenie "${eventTitle}"? Ta akcja jest nieodwracalna.`)) {
      return;
    }

    try {
      const result = await deleteEvent(eventId);
      if (result.success) {
        showMessage('success', 'Wydarzenie zostało usunięte');
        await loadEventsData();
      } else {
        showMessage('error', result.error || 'Błąd podczas usuwania wydarzenia');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      showMessage('error', 'Błąd podczas usuwania wydarzenia');
    }
  };

  // Instructors handlers
  const handleAddInstructor = () => {
    setEditingInstructorId(null);
    setShowInstructorForm(true);
  };

  const handleEditInstructor = (instructorId: string) => {
    setEditingInstructorId(instructorId);
    setShowInstructorForm(true);
  };

  const handleInstructorFormSuccess = async (instructor: Instructor) => {
    showMessage('success', editingInstructorId ? 'Instruktor został zaktualizowany' : 'Instruktor został dodany');
    setShowInstructorForm(false);
    setEditingInstructorId(null);
    await loadInstructorsData();
  };

  const handleInstructorFormCancel = () => {
    setShowInstructorForm(false);
    setEditingInstructorId(null);
  };

  const handleDeleteInstructor = async (instructorId: string, instructorName: string) => {
    if (!confirm(`Czy na pewno chcesz usunąć instruktora "${instructorName}"? Ta akcja jest nieodwracalna.`)) {
      return;
    }

    try {
      const result = await deleteInstructor(instructorId);
      if (result.success) {
        showMessage('success', 'Instruktor został usunięty');
        await loadInstructorsData();
      } else {
        showMessage('error', result.error || 'Błąd podczas usuwania instruktora');
      }
    } catch (error) {
      console.error('Error deleting instructor:', error);
      showMessage('error', 'Błąd podczas usuwania instruktora');
    }
  };

  // Trainings handlers
  const loadTrainingsData = async () => {
    try {
      setTrainingsLoading(true);
      const [trainingsResult, statsResult] = await Promise.all([
        getAllTrainings({ includeUnpublished: true }),
        getTrainingsStats(),
      ]);

      if (trainingsResult.success) {
        setTrainings(trainingsResult.data || []);
      }

      if (statsResult.success) {
        setTrainingsStats(statsResult.data);
      }
    } catch (error) {
      console.error('Error loading trainings data:', error);
      showMessage('error', 'Błąd podczas ładowania szkoleń medycznych');
    } finally {
      setTrainingsLoading(false);
    }
  };

  const handlePublishTraining = async (trainingId: string) => {
    try {
      const result = await publishTraining(trainingId);
      if (result.success) {
        showMessage('success', 'Szkolenie zostało opublikowane');
        await loadTrainingsData();
      } else {
        showMessage('error', result.error || 'Błąd podczas publikowania szkolenia');
      }
    } catch (error) {
      console.error('Error publishing training:', error);
      showMessage('error', 'Błąd podczas publikowania szkolenia');
    }
  };

  const handleCancelTraining = async (trainingId: string) => {
    try {
      const result = await cancelTraining(trainingId);
      if (result.success) {
        showMessage('success', 'Szkolenie zostało anulowane');
        await loadTrainingsData();
      } else {
        showMessage('error', result.error || 'Błąd podczas anulowania szkolenia');
      }
    } catch (error) {
      console.error('Error cancelling training:', error);
      showMessage('error', 'Błąd podczas anulowania szkolenia');
    }
  };

  const handleDeleteTraining = async (trainingId: string, trainingTitle: string) => {
    if (!confirm(`Czy na pewno chcesz usunąć szkolenie "${trainingTitle}"? Ta akcja jest nieodwracalna.`)) {
      return;
    }

    try {
      const result = await deleteTraining(trainingId);
      if (result.success) {
        showMessage('success', 'Szkolenie zostało usunięte');
        await loadTrainingsData();
      } else {
        showMessage('error', result.error || 'Błąd podczas usuwania szkolenia');
      }
    } catch (error) {
      console.error('Error deleting training:', error);
      showMessage('error', 'Błąd podczas usuwania szkolenia');
    }
  };

  return (
    <ModeratorOnly fallback={
      <Alert variant="outline" color="danger" className="m-8">
        <AlertDescription>
          Brak dostępu. Musisz być moderatorem lub administratorem.
        </AlertDescription>
      </Alert>
    }>
      <div className="admin-panel-layout premium-page-bg">
        <div className="premium-page-container admin-shell">
        {/* Sidebar Toggle (Mobile) */}
        <Button
          variant="surface"
          size="2"
          className={`sidebar-toggle ${isSidebarOpen ? 'is-open' : ''}`}
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label={isSidebarOpen ? 'Zamknij menu panelu' : 'Otwórz menu panelu'}
          aria-expanded={isSidebarOpen}
        >
          {isSidebarOpen ? (
            <X size={18} aria-hidden="true" />
          ) : (
            <PanelsTopLeft size={18} aria-hidden="true" />
          )}
          <span>{isSidebarOpen ? 'Zamknij menu' : 'Menu panelu'}</span>
        </Button>

        {/* Sidebar Backdrop (Mobile) */}
        {isSidebarOpen && (
          <div
            className="sidebar-backdrop"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h2 className="sidebar-title">Panel Zarządzania</h2>
            <p className="sidebar-subtitle">
              {user?.email || 'Administrator'}
            </p>
          </div>

          <nav className="sidebar-nav">
            <Button
              variant="ghost"
              className={`sidebar-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => {
                activateTab('dashboard');
                setIsSidebarOpen(false);
              }}
            >
              <BarChart3 className="nav-icon" size={20} aria-hidden="true" />
              <span className="nav-label">Dashboard</span>
            </Button>

            {isAdmin && (
              <Button
                variant="ghost"
                className={`sidebar-nav-item ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => {
                  activateTab('users');
                  setIsSidebarOpen(false);
                }}
              >
                <Users className="nav-icon" size={20} aria-hidden="true" />
                <span className="nav-label">Użytkownicy i Role</span>
              </Button>
            )}

            <Button
              variant="ghost"
              className={`sidebar-nav-item ${activeTab === 'library' ? 'active' : ''}`}
              onClick={() => {
                activateTab('library');
                setIsSidebarOpen(false);
              }}
            >
              <BookOpen className="nav-icon" size={20} aria-hidden="true" />
              <span className="nav-label">Biblioteka</span>
            </Button>

            {isAdmin && (
              <Button
                variant="ghost"
                className={`sidebar-nav-item ${activeTab === 'authors' ? 'active' : ''}`}
                onClick={() => {
                  activateTab('authors');
                  setIsSidebarOpen(false);
                }}
              >
                <UserCog className="nav-icon" size={20} aria-hidden="true" />
                <span className="nav-label">Autorzy</span>
              </Button>
            )}

            <Button
              variant="ghost"
              className={`sidebar-nav-item ${activeTab === 'events' ? 'active' : ''}`}
              onClick={() => {
                activateTab('events');
                setIsSidebarOpen(false);
              }}
            >
              <Calendar className="nav-icon" size={20} aria-hidden="true" />
              <span className="nav-label">Wydarzenia</span>
            </Button>

            <Button
              variant="ghost"
              className={`sidebar-nav-item ${activeTab === 'trainingCatalog' ? 'active' : ''}`}
              onClick={() => {
                activateTab('trainingCatalog');
                setIsSidebarOpen(false);
              }}
            >
              <ClipboardList className="nav-icon" size={20} aria-hidden="true" />
              <span className="nav-label">Szkolenia</span>
            </Button>

            <Button
              variant="ghost"
              className={`sidebar-nav-item ${activeTab === 'instructors' ? 'active' : ''}`}
              onClick={() => {
                activateTab('instructors');
                setIsSidebarOpen(false);
              }}
            >
              <GraduationCap className="nav-icon" size={20} aria-hidden="true" />
              <span className="nav-label">Instruktorzy</span>
            </Button>

            <Button
              variant="ghost"
              className={`sidebar-nav-item ${activeTab === 'shootingRanges' ? 'active' : ''}`}
              onClick={() => {
                activateTab('shootingRanges');
                setIsSidebarOpen(false);
              }}
            >
              <MapPin className="nav-icon" size={20} aria-hidden="true" />
              <span className="nav-label">Strzelnice</span>
            </Button>

            <Button
              variant="ghost"
              className={`sidebar-nav-item ${activeTab === 'trainings' ? 'active' : ''}`}
              onClick={() => {
                activateTab('trainings');
                setIsSidebarOpen(false);
              }}
            >
              <Heart className="nav-icon" size={20} aria-hidden="true" />
              <span className="nav-label">Grupy szkoleniowe</span>
            </Button>
          </nav>

          <div className="sidebar-footer">
            <div className="role-badges">
              <RoleBadgeList roles={roles.map(r => r.role_name as RoleName)} size="sm" />
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="admin-main">
          {message && (
            <Alert
              variant="surface"
              color={message.type === 'success' ? 'success' : 'danger'}
              className="mb-6"
            >
              <AlertDescription>{message.text}</AlertDescription>
            </Alert>
          )}

          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div className="admin-content">
              <div className="admin-content-header">
                <h1>Dashboard</h1>
                <p className="content-subtitle">Przegląd systemu i statystyki</p>
              </div>

              {/* Combined Stats */}
              <div className="admin-stats">
                {isAdmin && (
                  <>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">{users.length}</div>
                      <div className="admin-stat-label">Użytkowników</div>
                    </div>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">
                        {users.filter((u) => u.is_admin).length}
                      </div>
                      <div className="admin-stat-label">Administratorów</div>
                    </div>
                  </>
                )}
                {libraryStats && (
                  <>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">{libraryStats.totalBooks}</div>
                      <div className="admin-stat-label">Książek</div>
                    </div>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">{libraryStats.publishedBooks}</div>
                      <div className="admin-stat-label">Opublikowanych</div>
                    </div>
                  </>
                )}
              </div>

              {/* Quick Actions */}
              <div className="quick-actions">
                <h2>Szybkie akcje</h2>
                <div className="action-cards">
                  {isAdmin && (
                    <button
                      type="button"
                      className="action-card"
                      onClick={() => activateTab('users')}
                      aria-label="Przejdź do zarządzania użytkownikami"
                    >
                      <Users className="action-icon" size={24} aria-hidden="true" />
                      <h3>Zarządzaj użytkownikami</h3>
                      <p>Przypisuj role i zarządzaj dostępem</p>
                    </button>
                  )}
                  <button
                    type="button"
                    className="action-card"
                    onClick={() => {
                      activateTab('library');
                      handleAddBook();
                    }}
                    aria-label="Dodaj książkę"
                  >
                    <Plus className="action-icon" size={24} aria-hidden="true" />
                    <h3>Dodaj książkę</h3>
                    <p>Dodaj nową pozycję do biblioteki</p>
                  </button>
                  <button
                    type="button"
                    className="action-card"
                    onClick={() => activateTab('library')}
                    aria-label="Zarządzaj biblioteką"
                  >
                    <BookOpen className="action-icon" size={24} aria-hidden="true" />
                    <h3>Zarządzaj biblioteką</h3>
                    <p>Edytuj i publikuj książki</p>
                  </button>
                  <button
                    type="button"
                    className="action-card"
                    onClick={() => navigate('/wydarzenia/nowe')}
                    aria-label="Dodaj wydarzenie"
                  >
                    <Plus className="action-icon" size={24} aria-hidden="true" />
                    <h3>Dodaj wydarzenie</h3>
                    <p>Utwórz nowe wydarzenie</p>
                  </button>
                  <button
                    type="button"
                    className="action-card"
                    onClick={() => activateTab('events')}
                    aria-label="Zarządzaj wydarzeniami"
                  >
                    <Calendar className="action-icon" size={24} aria-hidden="true" />
                    <h3>Zarządzaj wydarzeniami</h3>
                    <p>Edytuj i publikuj wydarzenia</p>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Users Tab - Admin Only */}
          {activeTab === 'users' && (
          <AdminOnly fallback={
            <Alert variant="outline" color="danger">
              <AlertDescription>
                Zarządzanie użytkownikami dostępne tylko dla administratorów.
              </AlertDescription>
            </Alert>
          }>
            <div className="admin-content">
              <div className="admin-content-header">
                <h1>Użytkownicy i Role</h1>
                <p className="content-subtitle">Zarządzaj użytkownikami i ich uprawnieniami</p>
              </div>

              {loading ? (
                <div className="admin-loading">Ładowanie danych...</div>
              ) : (
                <>
                  {/* Statystyki */}
                  <div className="admin-stats">
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">{users.length}</div>
                      <div className="admin-stat-label">Użytkowników</div>
                    </div>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">
                        {users.filter((u) => u.is_admin).length}
                      </div>
                      <div className="admin-stat-label">Administratorów</div>
                    </div>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">
                        {users.filter((u) => u.is_moderator && !u.is_admin).length}
                      </div>
                      <div className="admin-stat-label">Moderatorów</div>
                    </div>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">
                        {users.filter((u) => !u.is_admin && !u.is_moderator).length}
                      </div>
                      <div className="admin-stat-label">Zwykłych użytkowników</div>
                    </div>
                  </div>

                  {/* Tabela użytkowników */}
                  <div className="admin-section">
              <h2>Użytkownicy</h2>

              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Imię i nazwisko</th>
                      <th>Role</th>
                      <th>Data rejestracji</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userData) => (
                      <tr key={userData.user_id}>
                        <td>{userData.email}</td>
                        <td>{userData.full_name || '—'}</td>
                        <td>
                          <RoleBadgeList
                            roles={
                              userData.roles.map((r: any) => r.role_name) as RoleName[]
                            }
                            size="sm"
                          />
                        </td>
                        <td>
                          {new Date(userData.user_created_at).toLocaleDateString('pl-PL')}
                        </td>
                        <td>
                          <Button
                            variant="solid"
                            size="1"
                            onClick={() => setSelectedUser(userData)}
                          >
                            Zarządzaj rolami
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>
                  </div>

                  {/* Modal zarządzania rolami - Radix UI Dialog */}
            <Dialog open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
              <DialogContent size="4">
                <DialogHeader>
                  <DialogTitle>Zarządzaj rolami użytkownika</DialogTitle>
                  <DialogDescription>
                    Przypisuj i usuwaj role dla wybranego użytkownika
                  </DialogDescription>
                </DialogHeader>

                {selectedUser && (
                  <div className="space-y-6">
                    <Card variant="surface" size="2">
                      <CardContent style={{ padding: 0 }}>
                        <p className="mb-2">
                          <strong>Email:</strong> {selectedUser.email}
                        </p>
                        <p>
                          <strong>Imię i nazwisko:</strong>{' '}
                          {selectedUser.full_name || '—'}
                        </p>
                      </CardContent>
                    </Card>

                    <div className="admin-roles-section">
                      <h4 className="text-lg font-semibold mb-3">Obecne role</h4>
                      <div className="admin-roles-list space-y-2">
                        {selectedUser.roles.length > 0 ? (
                            selectedUser.roles.map((role: any) => (
                              <div key={role.role_id} className="admin-role-item">
                                <RoleBadgeList roles={[role.role_name]} size="md" />
                                <Button
                                variant="solid"
                                size="1"
                                className="admin-btn-danger"
                                  onClick={() =>
                                    handleRemoveRole(selectedUser.user_id, role.role_name)
                                  }
                                  disabled={assigningRole}
                                >
                                  Usuń
                                </Button>
                              </div>
                            ))
                        ) : (
                            <p style={{ color: 'var(--text-white-70)' }}>Brak ról</p>
                        )}
                      </div>
                    </div>

                    <Separator />

                    <div className="admin-roles-section">
                      <h4 className="text-lg font-semibold mb-3">Przypisz rolę</h4>
                      <div className="admin-assign-roles space-y-3">
                        {availableRoles.map((role) => {
                          const hasRole = selectedUser.roles.some(
                            (r: any) => r.role_id === role.id
                          );

                          return (
                            <Card key={role.id} variant="surface" size="2" className="admin-role-option">
                              <CardContent className="flex items-center justify-between" style={{ padding: 0 }}>
                                <div className="admin-role-option-info">
                                    <strong className="text-base" style={{ color: 'var(--text-white-90)' }}>
                                      {role.display_name}
                                    </strong>
                                    <p className="text-sm mt-1" style={{ color: 'var(--text-white-70)' }}>
                                      {role.description}
                                    </p>
                                </div>
                                <Button
                                  variant={hasRole ? "outline" : "solid"}
                                  size="1"
                                  onClick={() =>
                                    handleAssignRole(selectedUser.user_id, role.name)
                                  }
                                  disabled={hasRole || assigningRole}
                                >
                                  {hasRole ? 'Przypisana' : 'Przypisz'}
                                </Button>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
                </>
              )}
            </div>
          </AdminOnly>
        )}

          {/* Authors Tab - Admin Only */}
          {activeTab === 'authors' && (
            <div className="admin-content">
              <AdminAuthorsTab embedded />
            </div>
          )}

          {/* Library Tab - Moderators and Admins */}
          {activeTab === 'library' && (
            <div className="admin-content">
              <div className="admin-content-header">
                <h1>Biblioteka</h1>
                <p className="content-subtitle">Zarządzaj książkami i publikacjami</p>
              </div>
            {showBookForm ? (
              <BookForm
                bookId={editingBookId || undefined}
                onSuccess={handleBookFormSuccess}
                onCancel={handleBookFormCancel}
              />
            ) : (
              <>
                {/* Library Stats */}
                {libraryStats && (
                  <div className="admin-stats">
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">{libraryStats.totalBooks}</div>
                      <div className="admin-stat-label">Wszystkich książek</div>
                    </div>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">{libraryStats.publishedBooks}</div>
                      <div className="admin-stat-label">Opublikowanych</div>
                    </div>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">{libraryStats.draftBooks}</div>
                      <div className="admin-stat-label">Szkiców</div>
                    </div>
                    <div className="admin-stat-card">
                      <div className="admin-stat-value">{libraryStats.categories.length}</div>
                      <div className="admin-stat-label">Kategorii</div>
                    </div>
                  </div>
                )}

                {/* Library Actions */}
                <div className="admin-section">
                  <div className="admin-section-header">
                    <h2>Zarządzanie Biblioteką</h2>
                    <Button variant="solid" onClick={handleAddBook}>
                      + Dodaj książkę
                    </Button>
                  </div>

                  {/* Books Table */}
                  {libraryLoading ? (
                    <div className="admin-loading">Ładowanie biblioteki...</div>
                  ) : books.length === 0 ? (
                    <div className="admin-empty-state">
                      <p>Biblioteka jest pusta</p>
                      <Button variant="solid" onClick={handleAddBook}>
                        Dodaj pierwszą książkę
                      </Button>
                    </div>
                  ) : (
                    <div className="admin-table-container">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Tytuł</th>
                            <th>Kategoria</th>
                            <th>Status</th>
                            <th>Rok wydania</th>
                            <th>Data dodania</th>
                            <th>Akcje</th>
                          </tr>
                        </thead>
                        <tbody>
                          {books.map((book) => (
                            <tr key={book.id}>
                              <td>
                                <strong>{book.title}</strong>
                                {book.description && (
                                  <div className="book-description">{book.description.substring(0, 100)}...</div>
                                )}
                              </td>
                              <td>
                                {book.book_categories && book.book_categories.length > 0
                                  ? book.book_categories.map((category) => category.name).join(', ')
                                  : '—'}
                              </td>
                              <td>
                                <span className={`status-badge status-${book.status}`}>
                                  {book.status === 'published' ? 'Opublikowana' :
                                   book.status === 'draft' ? 'Szkic' : 'Archiwum'}
                                </span>
                              </td>
                              <td>{book.publication_year || '—'}</td>
                              <td>{new Date(book.created_at).toLocaleDateString('pl-PL')}</td>
                              <td>
                                <div className="admin-table-actions">
                                  <Button
                                    variant="outline"
                                    size="1"
                                    onClick={() => handleEditBook(book.id)}
                                    title="Edytuj"
                                  >
                                    Edytuj
                                  </Button>
                                  {book.status === 'draft' && (
                                    <Button
                                      variant="solid"
                                      size="1"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => handlePublishBook(book.id)}
                                      title="Opublikuj"
                                    >
                                      Publikuj
                                    </Button>
                                  )}
                                  {book.status === 'published' && (
                                    <Button
                                      variant="outline"
                                      size="1"
                                      className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                                      onClick={() => handleArchiveBook(book.id)}
                                      title="Archiwizuj"
                                    >
                                      Archiwizuj
                                    </Button>
                                  )}
                                  <Button
                                    variant="solid"
                                    size="1"
                                    className="admin-btn-danger"
                                    onClick={() => handleDeleteBook(book.id, book.title)}
                                    title="Usuń"
                                  >
                                    Usuń
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
            </div>
          )}

          {/* Training Catalog Tab */}
          {activeTab === 'trainingCatalog' && (
            <div className="admin-content">
              <ModeratorOnly>
                <TrainingCatalogTab onToast={showMessage} />
              </ModeratorOnly>
            </div>
          )}

          {/* Instructors Tab */}
          {activeTab === 'instructors' && (
            <div className="admin-content">
              <ModeratorOnly>
                <>
                  <div className="admin-content-header">
                    <h2 className="admin-content-title">Zarządzanie Instruktorami</h2>
                    <Button
                      variant="solid"
                      onClick={handleAddInstructor}
                      className="flex items-center gap-2"
                    >
                      <Plus size={20} />
                      Dodaj instruktora
                    </Button>
                  </div>

                  {instructorsStats && (
                    <div className="library-stats">
                      <Card variant="surface" size="2">
                        <CardContent style={{ padding: 0 }}>
                          <div className="stat-value">{instructorsStats.totalInstructors}</div>
                          <div className="stat-label">Wszystkich instruktorów</div>
                        </CardContent>
                      </Card>
                      <Card variant="surface" size="2">
                        <CardContent style={{ padding: 0 }}>
                          <div className="stat-value">{instructorsStats.activeInstructors}</div>
                          <div className="stat-label">Aktywnych</div>
                        </CardContent>
                      </Card>
                      <Card variant="surface" size="2">
                        <CardContent style={{ padding: 0 }}>
                          <div className="stat-value">{instructorsStats.inactiveInstructors}</div>
                          <div className="stat-label">Nieaktywnych</div>
                        </CardContent>
                      </Card>
                      <Card variant="surface" size="2">
                        <CardContent style={{ padding: 0 }}>
                          <div className="stat-value">{instructorsStats.totalSpecializations?.length || 0}</div>
                          <div className="stat-label">Specjalizacji</div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  <div className="admin-section">
                    {showInstructorForm ? (
                      <InstructorForm
                        instructorId={editingInstructorId || undefined}
                        onSuccess={handleInstructorFormSuccess}
                        onCancel={handleInstructorFormCancel}
                      />
                    ) : (
                      <>
                        {instructorsLoading ? (
                          <div className="loading-state">Ładowanie instruktorów...</div>
                        ) : instructors.length === 0 ? (
                          <Alert>
                            <AlertDescription>
                              Brak instruktorów. Dodaj pierwszego instruktora klikając przycisk "Dodaj instruktora".
                            </AlertDescription>
                          </Alert>
                        ) : (
                          <div className="table-container">
                            <table className="admin-table">
                              <thead>
                                <tr>
                                  <th>Imię i nazwisko</th>
                                  <th>Email</th>
                                  <th>Specjalizacje</th>
                                  <th>Lata doświadczenia</th>
                                  <th>Status</th>
                                  <th>Akcje</th>
                                </tr>
                              </thead>
                              <tbody>
                                {instructors.map((instructor) => (
                                  <tr key={instructor.id}>
                                    <td>
                                      <strong>{instructor.name}</strong>
                                      {instructor.bio && (
                                        <div className="book-description">
                                          {instructor.bio.substring(0, 100)}...
                                        </div>
                                      )}
                                    </td>
                                    <td>{instructor.email || '—'}</td>
                                    <td>
                                      {instructor.specialization && instructor.specialization.length > 0
                                        ? instructor.specialization.join(', ')
                                        : '—'}
                                    </td>
                                    <td>{instructor.experience_years || '—'}</td>
                                    <td>
                                      <span className={`status-badge status-${instructor.is_active ? 'published' : 'draft'}`}>
                                        {instructor.is_active ? 'Aktywny' : 'Nieaktywny'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="admin-table-actions">
                                        <Button
                                          variant="outline"
                                          size="1"
                                          onClick={() => handleEditInstructor(instructor.id)}
                                          title="Edytuj"
                                        >
                                          Edytuj
                                        </Button>
                                        <Button
                                          variant="solid"
                                          size="1"
                                          className="admin-btn-danger"
                                          onClick={() => handleDeleteInstructor(instructor.id, instructor.name)}
                                          title="Usuń"
                                        >
                                          Usuń
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              </ModeratorOnly>
            </div>
          )}

          {/* Shooting Ranges Tab */}
          {activeTab === 'shootingRanges' && (
            <div className="admin-content">
              <ModeratorOnly>
                <ShootingRangesTab onToast={showMessage} />
              </ModeratorOnly>
            </div>
          )}

          {/* Trainings Tab */}
          {activeTab === 'trainings' && (
            <div className="admin-content">
              <ModeratorOnly>
                <>
                  <div className="admin-content-header">
                    <h2 className="admin-content-title">Grupy szkoleniowe</h2>
                    <Button
                      variant="solid"
                      onClick={() => {
                        openCreateTrainingGroup();
                      }}
                      className="flex items-center gap-2"
                    >
                      <Plus size={20} />
                      Dodaj grupę
                    </Button>
                  </div>

                  {trainingsStats && (
                    <div className="library-stats">
                      <Card variant="surface" size="2">
                        <CardContent style={{ padding: 0 }}>
                          <div className="stat-value">{trainingsStats.totalTrainings}</div>
                          <div className="stat-label">Wszystkie szkolenia</div>
                        </CardContent>
                      </Card>
                      <Card variant="surface" size="2">
                        <CardContent style={{ padding: 0 }}>
                          <div className="stat-value">{trainingsStats.publishedTrainings}</div>
                          <div className="stat-label">Opublikowane</div>
                        </CardContent>
                      </Card>
                      <Card variant="surface" size="2">
                        <CardContent style={{ padding: 0 }}>
                          <div className="stat-value">{trainingsStats.draftTrainings}</div>
                          <div className="stat-label">Szkice</div>
                        </CardContent>
                      </Card>
                      <Card variant="surface" size="2">
                        <CardContent style={{ padding: 0 }}>
                          <div className="stat-value">{trainingsStats.totalRegistrations}</div>
                          <div className="stat-label">Rejestracje</div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  <div className="admin-section">
                    {trainingsLoading ? (
                      <div className="loading-state">Ładowanie szkoleń...</div>
                    ) : trainings.length === 0 ? (
                      <Alert variant="surface" color="gray">
                        <AlertDescription>
                          Brak szkoleń medycznych. Dodaj pierwsze szkolenie klikając przycisk "Dodaj szkolenie".
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <div className="table-container">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Tytuł</th>
                              <th>Instruktor</th>
                              <th>Poziom</th>
                              <th>Data rozpoczęcia</th>
                              <th>Uczestnicy</th>
                              <th>Status</th>
                              <th>Akcje</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trainings.map((training) => (
                              <tr key={training.id}>
                                <td>
                                  <strong>{training.title}</strong>
                                  {training.description && (
                                    <div className="book-description">
                                      {training.description.substring(0, 100)}...
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {training.instructor?.name || '—'}
                                </td>
                                <td>
                                  <Badge variant="surface" color="gray" size="1">
                                    {training.difficulty_level}
                                  </Badge>
                                </td>
                                <td>
                                  {training.start_date
                                    ? new Date(training.start_date).toLocaleDateString('pl-PL', {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric',
                                      })
                                    : 'Data nieokreślona'}
                                </td>
                                <td>
                                  {training.current_participants} / {training.max_participants}
                                </td>
                                <td>
                                  <span className={`status-badge status-${training.status}`}>
                                    {training.status === 'published' ? 'Opublikowane' :
                                     training.status === 'draft' ? 'Szkic' :
                                     training.status === 'cancelled' ? 'Anulowane' : 'Zakończone'}
                                  </span>
                                </td>
                                <td>
                                  <div className="admin-table-actions">
                                    <Button
                                      variant="outline"
                                      size="1"
                                      onClick={() => {
                                        navigate(`/szkolenia-medyczne/${training.slug || training.id}`);
                                      }}
                                      title="Zobacz"
                                    >
                                      Zobacz
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="1"
                                      onClick={() => openRegistrations(training.id, training.title)}
                                      title="Zobacz zapisy"
                                    >
                                      Zapisy
                                    </Button>
                                    {training.status === 'draft' && (
                                      <Button
                                        variant="solid"
                                        size="1"
                                        className="bg-green-600 hover:bg-green-700"
                                        onClick={() => handlePublishTraining(training.id)}
                                        title="Opublikuj"
                                      >
                                        Publikuj
                                      </Button>
                                    )}
                                    {training.status === 'published' && (
                                      <Button
                                        variant="outline"
                                        size="1"
                                        className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                                        onClick={() => handleCancelTraining(training.id)}
                                        title="Anuluj"
                                      >
                                        Anuluj
                                      </Button>
                                    )}
                                    <Button
                                      variant="solid"
                                      size="1"
                                      className="admin-btn-danger"
                                      onClick={() => handleDeleteTraining(training.id, training.title)}
                                      title="Usuń"
                                    >
                                      Usuń
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              </ModeratorOnly>
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="admin-content">
              <ModeratorOnly>
              <>
                <div className="admin-content-header">
                  <h2 className="admin-content-title">Zarządzanie Wydarzeniami</h2>
                  <Button
                    variant="solid"
                    onClick={() => {
                      console.log('[AdminPanel] Navigate to /wydarzenia/nowe clicked');
                      navigate('/wydarzenia/nowe');
                    }}
                    className="flex items-center gap-2"
                  >
                    <Plus size={20} />
                    Dodaj wydarzenie
                  </Button>
                </div>

                {eventsStats && (
                  <div className="library-stats">
                    <Card variant="surface" size="2">
                      <CardContent style={{ padding: 0 }}>
                        <div className="stat-value">{eventsStats.totalEvents}</div>
                        <div className="stat-label">Wszystkie wydarzenia</div>
                      </CardContent>
                    </Card>
                    <Card variant="surface" size="2">
                      <CardContent style={{ padding: 0 }}>
                        <div className="stat-value">{eventsStats.upcomingEvents}</div>
                        <div className="stat-label">Nadchodzące</div>
                      </CardContent>
                    </Card>
                    <Card variant="surface" size="2">
                      <CardContent style={{ padding: 0 }}>
                        <div className="stat-value">{eventsStats.publishedEvents}</div>
                        <div className="stat-label">Opublikowane</div>
                      </CardContent>
                    </Card>
                    <Card variant="surface" size="2">
                      <CardContent style={{ padding: 0 }}>
                        <div className="stat-value">{eventsStats.totalAttendees}</div>
                        <div className="stat-label">Uczestnicy</div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                <div className="admin-section">
                  {eventsLoading ? (
                    <div className="loading-state">Ładowanie wydarzeń...</div>
                  ) : events.length === 0 ? (
                    <Alert variant="surface" color="gray">
                      <AlertDescription>
                        Brak wydarzeń. Dodaj pierwsze wydarzenie klikając przycisk "Dodaj wydarzenie".
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="table-container">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Tytuł</th>
                            <th>Data rozpoczęcia</th>
                            <th>Typ</th>
                            <th>Status</th>
                            <th>Uczestnicy</th>
                            <th>Akcje</th>
                          </tr>
                        </thead>
                        <tbody>
                          {events.map((event) => (
                            <tr key={event.id}>
                              <td>
                                <strong>{event.title}</strong>
                                {event.category && (
                                  <div className="book-description">{event.category}</div>
                                )}
                              </td>
                              <td>
                                {event.start_date 
                                  ? new Date(event.start_date).toLocaleDateString('pl-PL', { 
                                      day: '2-digit', 
                                      month: 'long', 
                                      year: 'numeric' 
                                    })
                                  : 'Data nieokreślona'
                                }
                              </td>
                              <td>
                                {event.is_virtual && event.location_name ? (
                                  <Badge variant="soft" color="warning" size="1">🌐📍 Hybrydowe</Badge>
                                ) : event.is_virtual ? (
                                  <Badge variant="soft" color="pink" size="1">🌐 Wirtualne</Badge>
                                ) : event.location_name ? (
                                  <Badge variant="surface" color="gray" size="1">📍 Stacjonarne</Badge>
                                ) : (
                                  <Badge variant="surface" color="gray" size="1">—</Badge>
                                )}
                              </td>
                              <td>
                                <span className={`status-badge status-${event.status}`}>
                                  {event.status === 'published' ? 'Opublikowane' :
                                   event.status === 'draft' ? 'Szkic' : 
                                   event.status === 'cancelled' ? 'Anulowane' : 'Zakończone'}
                                </span>
                              </td>
                              <td>
                                {event.current_attendees}
                                {event.max_attendees && ` / ${event.max_attendees}`}
                              </td>
                              <td>
                                <div className="admin-table-actions">
                                  <Button
                                    variant="outline"
                                    size="1"
                                    onClick={() => {
                                      console.log('[AdminPanel] Navigate to event:', event.slug || event.id);
                                      navigate(`/wydarzenia/${event.slug || event.id}`);
                                    }}
                                    title="Zobacz"
                                  >
                                    Zobacz
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="1"
                                    onClick={() => {
                                      console.log('[AdminPanel] Navigate to edit event:', event.id);
                                      navigate(`/wydarzenia/edytuj/${event.id}`);
                                    }}
                                    title="Edytuj"
                                  >
                                    Edytuj
                                  </Button>
                                  {event.status === 'draft' && (
                                    <Button
                                      variant="solid"
                                      size="1"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => handlePublishEvent(event.id)}
                                      title="Opublikuj"
                                    >
                                      Publikuj
                                    </Button>
                                  )}
                                  {event.status === 'published' && (
                                    <Button
                                      variant="outline"
                                      size="1"
                                      className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                                      onClick={() => handleCancelEvent(event.id)}
                                      title="Anuluj"
                                    >
                                      Anuluj
                                    </Button>
                                  )}
                                  <Button
                                    variant="solid"
                                    size="1"
                                    className="admin-btn-danger"
                                    onClick={() => handleDeleteEvent(event.id, event.title)}
                                    title="Usuń"
                                  >
                                    Usuń
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
              </ModeratorOnly>
            </div>
          )}
        </main>

        {/* Create Training Group Dialog */}
        <Dialog
          open={createTrainingDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              setCreateTrainingDialogOpen(false);
              setCreatingTraining(false);
            }
          }}
        >
          <DialogContent size="4">
            <DialogHeader>
              <DialogTitle>Dodaj grupę szkoleniową</DialogTitle>
              <DialogDescription>
                Instruktor + typ szkolenia + data + strzelnica/miejsce + cena
              </DialogDescription>
            </DialogHeader>

            <Card variant="surface">
              <CardContent className="space-y-3">
                <div>
                  <Label>Tytuł *</Label>
                  <Input
                    value={trainingForm.title}
                    onChange={(e) => setTrainingForm((s) => ({ ...s, title: e.target.value }))}
                    placeholder="np. Strzelanie dynamiczne - poziom 1"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label>Publikuj od razu</Label>
                  <Switch
                    checked={trainingForm.status === 'published'}
                    onCheckedChange={(checked) =>
                      setTrainingForm((s) => ({ ...s, status: checked ? 'published' : 'draft' }))
                    }
                  />
                </div>

                <div>
                  <Label>Typ szkolenia</Label>
                  <Select
                    value={trainingForm.type_id || 'none'}
                    onValueChange={(value) =>
                      setTrainingForm((s) => ({ ...s, type_id: value === 'none' ? '' : value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wybierz typ (opcjonalnie)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {trainingFormTypes.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Instruktor</Label>
                  <Select
                    value={trainingForm.instructor_id || 'none'}
                    onValueChange={(value) =>
                      setTrainingForm((s) => ({ ...s, instructor_id: value === 'none' ? '' : value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Wybierz instruktora (opcjonalnie)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {trainingFormInstructors.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Poziom</Label>
                  <Select
                    value={trainingForm.difficulty_level}
                    onValueChange={(value) =>
                      setTrainingForm((s) => ({
                        ...s,
                        difficulty_level: value as any,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="podstawowy">Podstawowy</SelectItem>
                      <SelectItem value="średni">Średni</SelectItem>
                      <SelectItem value="zaawansowany">Zaawansowany</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Data rozpoczęcia *</Label>
                  <Input
                    type="datetime-local"
                    value={trainingForm.start_date}
                    onChange={(e) => setTrainingForm((s) => ({ ...s, start_date: e.target.value }))}
                  />
                </div>

                <div>
                  <Label>Data zakończenia *</Label>
                  <Input
                    type="datetime-local"
                    value={trainingForm.end_date}
                    onChange={(e) => setTrainingForm((s) => ({ ...s, end_date: e.target.value }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label>Waitlista</Label>
                  <Switch
                    checked={trainingForm.is_waitlist_enabled}
                    onCheckedChange={(checked) =>
                      setTrainingForm((s) => ({ ...s, is_waitlist_enabled: checked }))
                    }
                  />
                </div>

                <div>
                  <Label>Cena (PLN)</Label>
                  <Input
                    type="number"
                    value={trainingForm.price_amount}
                    onChange={(e) => setTrainingForm((s) => ({ ...s, price_amount: Number(e.target.value) }))}
                    min={0}
                    step={1}
                  />
                </div>

                <div>
                  <Label>Maks. uczestników</Label>
                  <Input
                    type="number"
                    value={trainingForm.max_participants}
                    onChange={(e) => setTrainingForm((s) => ({ ...s, max_participants: Number(e.target.value) }))}
                    min={1}
                    step={1}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label>Wybierz strzelnicę z listy</Label>
                  <Switch
                    checked={trainingForm.use_range}
                    onCheckedChange={(checked) => setTrainingForm((s) => ({ ...s, use_range: checked }))}
                  />
                </div>

                {trainingForm.use_range ? (
                  <div>
                    <Label>Strzelnica</Label>
                    <Select
                      value={trainingForm.shooting_range_id || 'none'}
                      onValueChange={(value) => {
                        if (value === 'none') {
                          setTrainingForm((s) => ({
                            ...s,
                            shooting_range_id: '',
                            location_name: '',
                            location_address: '',
                            location_city: '',
                            location_country: 'Poland',
                          }));
                          return;
                        }
                        applySelectedRangeToLocation(value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Wybierz strzelnicę" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {trainingFormRanges.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                <div>
                  <Label>Miejsce szkolenia *</Label>
                  <Input
                    value={trainingForm.location_name}
                    onChange={(e) => setTrainingForm((s) => ({ ...s, location_name: e.target.value }))}
                    placeholder="np. Strzelnica XYZ / Poligon ..."
                  />
                </div>

                <div>
                  <Label>Adres</Label>
                  <Input
                    value={trainingForm.location_address}
                    onChange={(e) => setTrainingForm((s) => ({ ...s, location_address: e.target.value }))}
                  />
                </div>

                <div>
                  <Label>Miasto</Label>
                  <Input
                    value={trainingForm.location_city}
                    onChange={(e) => setTrainingForm((s) => ({ ...s, location_city: e.target.value }))}
                  />
                </div>

                <div>
                  <Label>Kraj</Label>
                  <Input
                    value={trainingForm.location_country}
                    onChange={(e) => setTrainingForm((s) => ({ ...s, location_country: e.target.value }))}
                  />
                </div>
              </CardContent>
            </Card>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={creatingTraining}>
                  Anuluj
                </Button>
              </DialogClose>
              <Button variant="solid" onClick={submitCreateTrainingGroup} disabled={creatingTraining}>
                {creatingTraining ? 'Zapisywanie...' : 'Utwórz'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Registrations Dialog */}
        <Dialog
          open={!!registrationsDialog?.open}
          onOpenChange={(open) => {
            if (!open) {
              setRegistrationsDialog(null);
              setRegistrations([]);
              setRegistrationsError(null);
              setRegistrationsLoading(false);
            }
          }}
        >
          <DialogContent size="4">
            <DialogHeader>
              <DialogTitle>Zapisy na szkolenie</DialogTitle>
              <DialogDescription>
                {registrationsDialog?.trainingTitle || '—'}
              </DialogDescription>
            </DialogHeader>

            {registrationsLoading ? (
              <div className="admin-loading">Ładowanie zapisów…</div>
            ) : registrationsError ? (
              <Alert variant="outline" color="danger">
                <AlertDescription>{registrationsError}</AlertDescription>
              </Alert>
            ) : registrations.length === 0 ? (
              <Alert variant="surface" color="gray">
                <AlertDescription>Brak zapisów.</AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="admin-stats" style={{ marginTop: '1rem' }}>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">{registrations.length}</div>
                    <div className="admin-stat-label">Wszystkie</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">
                      {registrations.filter(r => r.registration_status === 'confirmed').length}
                    </div>
                    <div className="admin-stat-label">Potwierdzone</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">
                      {registrations.filter(r => r.registration_status === 'registered').length}
                    </div>
                    <div className="admin-stat-label">W trakcie (hold)</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">
                      {registrations.filter(r => r.registration_status === 'waitlisted').length}
                    </div>
                    <div className="admin-stat-label">Waitlista</div>
                  </div>
                </div>

                <div className="admin-table-container" style={{ marginTop: '1rem' }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Użytkownik</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Płatność</th>
                        <th>Kwota</th>
                        <th>Waitlista</th>
                        <th>Hold do</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map((r) => (
                        <tr key={r.id}>
                          <td>{r.full_name || r.nickname || r.user_id}</td>
                          <td>{r.email || '—'}</td>
                          <td>
                            <Badge variant="surface" color="gray" size="1">{r.registration_status}</Badge>
                          </td>
                          <td>
                            <Badge variant="surface" color="gray" size="1">{r.payment_status}</Badge>
                          </td>
                          <td>{typeof r.payment_amount === 'number' ? `${r.payment_amount.toFixed(2)} PLN` : '—'}</td>
                          <td>{r.waitlist_position ?? '—'}</td>
                          <td>
                            {r.reservation_expires_at
                              ? new Date(r.reservation_expires_at).toLocaleString('pl-PL')
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="solid">Zamknij</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Confirm Dialogs */}
      {removeRoleDialog && (
        <ConfirmDialog
          open={removeRoleDialog.open}
          onOpenChange={(open) => !open && setRemoveRoleDialog(null)}
          title={`Usuń rolę ${removeRoleDialog.roleName}?`}
          description={`Czy na pewno chcesz usunąć rolę ${removeRoleDialog.roleName}? Użytkownik straci wszystkie uprawnienia związane z tą rolą.`}
          confirmLabel="Usuń rolę"
          cancelLabel="Anuluj"
          confirmVariant="destructive"
          onConfirm={confirmRemoveRole}
        />
      )}

      {deleteBookDialog && (
        <ConfirmDialog
          open={deleteBookDialog.open}
          onOpenChange={(open) => !open && setDeleteBookDialog(null)}
          title="Usuń książkę?"
          description={`Czy na pewno chcesz usunąć książkę "${deleteBookDialog.bookTitle}"? Ta akcja jest nieodwracalna.`}
          confirmLabel="Usuń"
          cancelLabel="Anuluj"
          confirmVariant="destructive"
          onConfirm={confirmDeleteBook}
        />
      )}
    </ModeratorOnly>
  );
}

